import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import { getDefaults } from '~appointments/utils/defaults';
import { DexFieldMap } from '~dex/types';
import classNames from 'classnames';

import { AuthTags } from '~constants/authTags';
import { CASE_MANAGEMENT_NOTE_TYPE_ID } from '~constants/common';
import { getMissingContent } from '~constants/notFoundMap';
import { tponeUrl } from '~constants/tpone';

import { useAllSearchParams } from '~libs/searchParams';
import { useToast } from '~libs/toast';

import ConfirmDialog from '~components/ConfirmDialog';
import ContainerLoading from '~components/ContainerLoading';
import ErrorResponse from '~components/ErrorResponse';
import { FieldProps } from '~components/form-fields';
import PageNotFound from '~components/PageNotFound';
import TabContainer from '~components/TabContainer';
import { AppointmentBanner, AppointmentDeclined, AppointmentForm } from '~appointments/components';
import AppointmentDexTab, { toApiBody, toFormValues } from '~appointments/components/AppointmentDexTab';
import DexCopyConfirmDialog from '~appointments/components/DexCopyConfirmDialog';
import GroupAppointmentAttendees from '~appointments/components/group/GroupAppointmentAttendees';
import GroupAppointmentSettings from '~appointments/components/group/GroupAppointmentSettings';
import GroupAppointmentSummary from '~appointments/components/group/GroupAppointmentSummary';

import { get } from '~appointments/forms';

import { getAll as getActivities } from '~appointments/actions/activities';
import { addGroupChildren, create, getOne, getSignature, update } from '~appointments/actions/appointments';
import { getAll as getCareQualificationLevels } from '~appointments/actions/careQualificationLevels';
import { DexDetails, dexDetailsApi, DexDetailsBody } from '~appointments/actions/dexDetails';
import { getAll as getLocations } from '~appointments/actions/locations';
import { getAll as getRepeatFrequencies } from '~appointments/actions/repeatFrequencies';
import { getAll as getServiceTypes } from '~appointments/actions/serviceTypes';
import { getAll as getAppointmentStatuses } from '~appointments/actions/statuses';
import { getAvailability, getAll as getCareWorkers, getOne as getOneCareWorker } from '~care-workers/actions';
import { useGetDexFieldMapQuery } from '~dex/actions/dex';
import { getAll as getEquipments } from '~lookups/actions/equipments';
import { getAll as getLeaveCodes } from '~lookups/actions/leaveCodes';
import { getAll as getMedications } from '~lookups/actions/medications';
import { getAll as getPaymentTypes } from '~lookups/actions/paymentTypes';
import { getAll as getTaskClassifications } from '~lookups/actions/taskClassifications';
import { getAll as getTaskTypes } from '~lookups/actions/taskTypes';
import { getAll as getClients, getOne as getOneClient } from '~main/actions/clients';
import { getAll as getAllFormDefaults } from '~main/actions/formDefaults';
import { generateSession, getFeatureFlag, setTitle } from '~main/actions/login';

import {
  clearAppointment,
  clearChildAppointment,
  clearCopyAppointment,
  clearError,
} from '~appointments/reducers/appointments';
import { clearQualifications } from '~appointments/reducers/careQualificationLevels';
import { clearUserAppointments } from '~appointments/reducers/userAppointments';

import {
  selectAppointmentLoading,
  selectAppointmentRowsLoading,
  selectChildAppointment,
  selectCopyAppointment,
  selectCopyDexSourceId,
  selectCopyDexSourceReference,
  selectCurrentAppointment,
  selectErrorResponse,
  selectIsCurrentAppointmentChspFunded,
} from '~appointments/selectors/appointments';
import { selectAppointmentServiceTypes } from '~appointments/selectors/serviceTypes';
import { selectCurrentUser } from '~care-workers/selectors/users';
import { selectCurrentClient } from '~main/selectors/clients/clients';
import { selectFormDefaults } from '~main/selectors/formDefaults';
import {
  selectAuthenticatedUser,
  selectCompanyDefaults,
  selectCompanySettings,
  selectUserTags,
} from '~main/selectors/login';

interface ComponentProps {
  isNew?: boolean;
  isTransport?: boolean;
  forceRepeat?: boolean;
  isSupportCoordination?: boolean;
  isCaseManagement?: boolean;
  isNewGroupParent?: boolean;
  isNewGroupChild?: boolean;
  participantType?: 'user' | 'client';
}

// Index of the DEX tab within singleAppointmentTabs — shared by the query-gating check and the
// ?tab=dex deep-link effect so the two can't drift independently if a tab is ever inserted before it.
const DEX_TAB_INDEX = 1;

const Appointment: React.FC<ComponentProps> = ({
  isNew = false,
  isTransport = false,
  forceRepeat = false,
  isSupportCoordination = false,
  isCaseManagement: forceCaseManagement = false,
  isNewGroupParent = false,
  isNewGroupChild = false,
  participantType = null,
}) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const { id, group_id } = useParams();
  const searchParamValues = useAllSearchParams();
  const REDIRECT_URL = tponeUrl;

  // Initialised synchronously from the URL (not via a later effect) so a `?tab=dex` deep link never
  // has a render where activeTab is still its default 0 — that gap is what let the Details-only
  // reference data load unconditionally before the deep link could be applied.
  const [activeTab, setActiveTab] = useState<number>(searchParamValues.tab === 'dex' ? DEX_TAB_INDEX : 0);

  // Validation fields and confirmation messages to display
  const [changedValues, setChangedValues]: [any, any] = useState({});
  const [confirmMessages, setConfirmMessages]: [string[], any] = useState([]);
  const [confirmOpen, setConfirmOpen]: [boolean, any] = useState(false);
  const [crmNoteOpen, setCrmNoteOpen]: [boolean, any] = useState(false);
  const [pendingSaveValues, setPendingSaveValues]: [any, any] = useState(null);
  const [isRedirecting, setIsRedirecting]: [boolean, any] = useState(false);

  const [fields, setFields]: [FieldProps, any] = useState({});

  // - DEX carried over by Create Similar, held here until the appointment is created.
  // `dexSourceValues` seeds the draft tab from the source appointment; `dexDraft` is whatever the
  // user has since made of it and is what actually gets saved; `draftServiceTypeId` tracks the live
  // form's selected service type so the draft tab can resolve its field set from it.
  const [dexSourceValues, setDexSourceValues] = useState<DexDetails | null>(null);
  const [dexDraft, setDexDraft] = useState<Partial<DexDetailsBody> | null>(null);
  // The field tags the draft tab rendered, so the copy confirmation lists the same fields the user
  // saw rather than every DEX column toApiBody defaults.
  const [dexDraftFieldTags, setDexDraftFieldTags] = useState<string[]>([]);
  // Formik's validity for the draft tab. A draft has no submit of its own to gate on, so the
  // appointment's own save is where the tab's required-field rules have to be enforced.
  const [isDexDraftValid, setIsDexDraftValid] = useState(true);
  // Only the one field the draft DEX tab actually needs from the live form - not the whole values
  // object. Formik reports the whole object on every keystroke; publishing just the id means
  // setDraftServiceTypeId is called with the same primitive on unrelated keystrokes, so React bails
  // out of the re-render (and the unmemoised AppointmentForm body/Yup-schema/initial-values work
  // that would otherwise re-run on every character typed elsewhere on the form).
  const [draftServiceTypeId, setDraftServiceTypeId] = useState<string | number | null>(null);
  const [dexConfirmOpen, setDexConfirmOpen] = useState(false);
  const [pendingDexCreateValues, setPendingDexCreateValues] = useState<any>(null);

  const appointment = useAppSelector(selectCurrentAppointment);
  const copyValues = useAppSelector(selectCopyAppointment);
  const copyDexSourceId = useAppSelector(selectCopyDexSourceId);
  const copyDexSourceReference = useAppSelector(selectCopyDexSourceReference);

  // The in-flight source-DEX load, and the value it settled on. Saving before it resolves would
  // send no dex_details and show no confirmation, silently dropping the copied data - so the save
  // awaits this rather than racing it. Held in refs, not state: the save path needs the settled
  // value in the same tick it resumes, before React has necessarily re-rendered with it.
  const sourceDexLoadRef = useRef<Promise<void> | null>(null);
  const sourceDexSettleRef = useRef<(() => void) | null>(null);
  const dexSourceValuesRef = useRef<DexDetails | null>(null);
  const dexDraftRef = useRef<Partial<DexDetailsBody> | null>(null);
  // The target's field map, and a promise that settles when it first resolves. Armed during render
  // for the same reason the source load is: the save has to be able to wait for it, and a value
  // only present in the render closure is stale by the time an awaited save resumes.
  const draftFieldMapRef = useRef<DexFieldMap | null | undefined>(undefined);
  const fieldMapLoadRef = useRef<Promise<void> | null>(null);
  const fieldMapSettleRef = useRef<(() => void) | null>(null);

  // Armed during render, not in the effect below. useEffect is passive and React does not promise
  // to run it before a submit event, so a ref assigned only there can still be null when the save
  // handler looks - which would skip the wait and drop the copy, the very race this closes. The
  // effect settles it; creating it here is idempotent because the ref guards it.
  if (isNew && copyDexSourceId && sourceDexLoadRef.current === null) {
    sourceDexLoadRef.current = new Promise<void>((resolve) => {
      sourceDexSettleRef.current = resolve;
    });
    fieldMapLoadRef.current = new Promise<void>((resolve) => {
      fieldMapSettleRef.current = resolve;
    });
  }

  const childValues = useAppSelector(selectChildAppointment);
  const selectAppointmentsRowLoading = useAppSelector(selectAppointmentRowsLoading);
  const selectAppointmentsLoading = useAppSelector(selectAppointmentLoading);
  const serviceTypes = useAppSelector(selectAppointmentServiceTypes);
  const hasDexTab = useAppSelector(selectIsCurrentAppointmentChspFunded);
  const companyDefaults = useAppSelector(selectCompanyDefaults);
  const companyFormDefaults = useAppSelector(selectFormDefaults);
  const defaultClient = useAppSelector(selectCurrentClient);

  const companySettings = useAppSelector(selectCompanySettings);
  const defaultUser = useAppSelector(selectCurrentUser);
  const tags = useAppSelector(selectUserTags);
  const authenticatedUser = useAppSelector(selectAuthenticatedUser);

  const isLoading = isNew
    ? selectAppointmentsLoading === 'pending'
    : selectAppointmentsRowLoading === 'pending' || selectAppointmentsRowLoading === 'idle';
  const isRepeat = appointment?.repeat ?? forceRepeat;
  const isDeclined = appointment?.declined;
  const isPendingDeclined = isDeclined && appointment?.chargeable_cancellation_actioned === false;
  const canViewActivities = tags.includes(AuthTags.APPOINTMENTS.ACTIVITIES.VIEW);
  const errorResponse = useAppSelector(selectErrorResponse);

  const isExistingGroupParent = appointment?.group_session_id === 1;
  const isExistingGroupChild = appointment?.group_session_id > 1;
  const isClientGroupChild =
    isExistingGroupChild && appointment?.user_id === companySettings.SYSTEM_2022_GROUP_SESSION_USER_ID;
  const isNewGroup = isNewGroupParent || isNewGroupChild;
  const isCaseManagement = appointment?.case_management ?? forceCaseManagement;
  const isGroup = isNewGroup || isExistingGroupParent || isExistingGroupChild;
  const isGroupParent = isNewGroupParent || isExistingGroupParent;

  // True once the Details tab should actually be considered viewed: always for a new appointment or
  // one with no DEX tab to defer to (non-CHSP-funded, or a group-parent appointment — which never
  // shows a DEX tab regardless of funding, so a stray `?tab=dex` on one must not get stuck deferring
  // forever), otherwise only once the deep-linked tab has been resolved and Details is the one
  // selected.
  const isDeepLinkedToDex = searchParamValues.tab === 'dex';
  const isDetailsActive =
    isNew || !isDeepLinkedToDex || (!!appointment && (!hasDexTab || isGroupParent)) || activeTab === 0;

  // One-way latch: once Details has been genuinely viewed, its content (AppointmentForm and every
  // field it renders — including ones with their own independent data fetching, like
  // FormInputClientHoldCheck/FormInputPackageSelector) stays mounted forever, even if the user
  // switches back to DEX. This is what actually stops those fields' own fetches on a `?tab=dex`
  // deep link (isActive/skip alone only covers fetches this file dispatches directly) — while never
  // unmounting Details once shown, so an in-progress edit is never silently discarded.
  const [hasMountedDetailsContent, setHasMountedDetailsContent] = useState<boolean>(isDetailsActive);
  useEffect(() => {
    if (isDetailsActive && !hasMountedDetailsContent) setHasMountedDetailsContent(true);
  }, [isDetailsActive, hasMountedDetailsContent]);

  const { SYSTEM_SupportCoordinationServiceTypeID } = companySettings;
  const serviceTypeParams =
    SYSTEM_SupportCoordinationServiceTypeID &&
    (Array.isArray(SYSTEM_SupportCoordinationServiceTypeID) || !isNaN(Number(SYSTEM_SupportCoordinationServiceTypeID)))
      ? SYSTEM_SupportCoordinationServiceTypeID
      : null;

  const appointmentType = (() => {
    switch (true) {
      case isCaseManagement:
        return 'case-management';
      case isSupportCoordination:
        return 'support-coordination';
      case isGroup:
        return 'group';
      default:
        return 'single';
    }
  })();

  const supportCoordinationId = companyDefaults?.support_coordination_service_id;
  const formDefaultDependencies = { companySettings };
  const formDefaults = getDefaults(appointmentType, formDefaultDependencies, companyFormDefaults);

  useEffect(() => {
    // Get appointment by ID on mount if new. For an existing appointment, only fetch the
    // appointment itself here — everything the Details tab needs is deferred (see loadDetailsData)
    // so a page load that lands directly on another tab doesn't fire it unnecessarily.
    if (isNew) {
      loadCommon();
      loadNew();
    } else {
      dispatch(getOne({ id, include_repeat_next_date: true }));
    }
    // clear any old errors
    dispatch(clearError());

    return () => {
      dispatch(clearQualifications([]));
      dispatch(clearAppointment());
      dispatch(clearUserAppointments());

      if (isNew) {
        dispatch(clearCopyAppointment());
        dispatch(clearChildAppointment());
      }
    };
  }, []);

  useEffect(() => {
    if (isSupportCoordination && !tags.includes(AuthTags.APPOINTMENTS.SUPPORTCOORDINATION.UPDATE)) {
      navigate('/access-denied?type=1');
    }
    if (isCaseManagement && !tags.includes(AuthTags.APPOINTMENTS.CASEMANAGEMENT.UPDATE)) {
      navigate('/access-denied?type=1');
    }
  }, [tags]);

  // Page-wide, regardless of tab: the legacy group-session redirect check and the browser tab title.
  useEffect(() => {
    if (appointment && !isNew) {
      const redirect = async () => {
        if (appointment?.is_group_session === true) {
          const { payload } = await dispatch(getFeatureFlag({ flag: 'feature-group-appointments' }));
          const { data: isEnabled } = payload;

          if (!isEnabled) {
            setIsRedirecting(true);
            window.location.replace(`${REDIRECT_URL}/appointment-edit.asp?eid=${id}`);
          }
        }
      };
      redirect();

      setTitle(`Appt #${appointment?.reference_number ?? ''}, ${appointment?.client?.full_name ?? ''}`);
    }
  }, [appointment, copyValues]);

  // Details-tab-only: feeds FormInputGroupParticipants/FormInputWorkerAvailability/FormInputImage.
  // Deferred behind hasMountedDetailsContent for the same reason as loadDetailsData below.
  useEffect(() => {
    if (!appointment || isNew || !hasMountedDetailsContent) return;

    dispatch(getClients(isGroup ?? { id: appointment.client_id }));

    if (appointment.user_id) {
      dispatch(
        getAvailability({
          id: appointment.user_id,
          appointment_date: appointment.date,
        }),
      );
    }

    if (appointment.photos?.length > 0) {
      dispatch(generateSession());
    }
  }, [appointment, copyValues, hasMountedDetailsContent, isNew]);

  const loadCommon = () => {
    dispatch(getRepeatFrequencies());
    dispatch(getAppointmentStatuses());
    dispatch(getServiceTypes(isSupportCoordination ? { id: serviceTypeParams } : undefined));
    dispatch(getCareQualificationLevels({ appt_match: true }));
    dispatch(getLeaveCodes());
    dispatch(getPaymentTypes());
    dispatch(getTaskClassifications());
    dispatch(getTaskTypes({ disabled: false, sort: ['classification', 'name'] }));
    dispatch(getMedications({ disabled: false, sort: ['name'] }));
    dispatch(getEquipments({ disabled: false, sort: ['name'] }));
    dispatch(getLocations());
  };

  const loadExisting = () => {
    dispatch(getOne({ id, include_repeat_next_date: true }));
    dispatch(getSignature({ id }));
    dispatch(getActivities({ parent_id: id, sort: ['id', 'date_time'] }));
    dispatch(getCareWorkers());
  };

  // Details-tab-only reference data for an existing appointment (repeat frequencies, statuses,
  // service types, lookups, plus this appointment's signature/activities/care-worker list). Deferred
  // until the Details tab is actually viewed (see isDetailsActive below) so opening straight into
  // another tab — e.g. a `?tab=dex` deep link from the DEX Readiness Report — doesn't also fire the
  // full appointment-editing lookup set for a tab the user never opens.
  const loadDetailsData = () => {
    loadCommon();
    dispatch(getSignature({ id }));
    dispatch(getActivities({ parent_id: id, sort: ['id', 'date_time'] }));
    dispatch(getCareWorkers());
  };

  const loadNew = () => {
    setTitle('New Appointment');
    dispatch(getServiceTypes(isSupportCoordination ? { id: serviceTypeParams } : undefined));
    dispatch(getCareWorkers());
    dispatch(getClients());
    dispatch(getAllFormDefaults({ feature_tag: 'appointments' }));

    const { copy_id, parent_id } = searchParamValues;
    if (copy_id) {
      dispatch(getOne({ id: copy_id, isTemplate: true, include_repeat_next_date: true }));
    } else if (parent_id) {
      dispatch(getOne({ id: parent_id, isParent: true, include_repeat_next_date: true }));
    } else if (participantType) {
      dispatch(getOne({ id: group_id, isChild: true, include_repeat_next_date: true }));
      dispatch(getOneCareWorker({ id: companySettings.SYSTEM_2022_GROUP_SESSION_USER_ID }));
      dispatch(getOneClient({ id: companySettings.SYSTEM_2022_GROUP_SESSION_CLIENT_ID }));
    }
  };

  const onConfirm = (confirmed: boolean, values: any) => {
    setConfirmOpen(false);
    setConfirmMessages([]);

    if (confirmed) save(values);
  };

  const canSave = () => {
    if (appointment) {
      const { exported_payroll, exported_invoices } = appointment;
      if (!authenticatedUser?.accounts_access_enabled && (exported_payroll || exported_invoices)) {
        return false;
      }
    }
    return true;
  };

  const onSave = async (values: any, originalValues: any) => {
    if (validateConfirm(values, originalValues)) {
      save(values);
    } else {
      setChangedValues(values);
      setConfirmOpen(true);
    }
  };

  // Whether a source appointment's DEX values were actually eligible to carry over - distinct from
  // `dexDraft`/`dexDetailsToSave` being non-null. Once the draft tab's Formik form mounts,
  // DexDraftPublisher always publishes SOME body (toApiBody never itself returns null), even when
  // the skip rules below left nothing seeded (excluded service type, no DEX config, or a source DEX
  // tab that was never filled in). So `dexDraft` truthiness can't distinguish "genuinely copied
  // values" from "the draft tab's own untouched defaults" - only `dexSourceValues` (set exclusively
  // by the skip-rule check in the effect above) can.

  // DEX session data is reported to DSS. Where it was copied from another appointment the
  // person saving is told so explicitly, and names the source, before it is written on their behalf.
  // Gated on hasEligibleDexCopy (not merely dexDetailsToSave being non-null) so a skip-eligible
  // source - one whose own DEX tab was never filled in, is excluded, or has no DEX config - never
  // produces a false "this was copied from #X" confirmation over the draft tab's own defaults.
  // Requires something actually being written, not merely an eligible source: if the target's own
  // DEX type is excluded or unconfigured, dexDetailsToSave is null and there is no copy to confirm.
  // Asking someone to approve a copy that will not happen is worse than not asking.
  const requiresDexCopyConfirmation = () =>
    isNew && !!copyDexSourceId && !!resolveDexDetailsForSave() && dexSourceValuesRef.current !== null;

  const onSaveWithDexConfirmation = async (values: any, originalValues: any) => {
    // Wait for both the source's DEX data and the target's field map rather than racing them.
    // Without this, a save fired before either settles sends no dex_details and shows no
    // confirmation - the copy vanishes silently.
    if (sourceDexLoadRef.current) await sourceDexLoadRef.current;
    if (fieldMapLoadRef.current) await fieldMapLoadRef.current;

    // The draft tab's own Save is disabled by design, so Formik's submit never runs and its
    // required-field rules would otherwise not apply to the one path that writes DSS-reported data.
    // Enforced here instead, where the values actually get written.
    if (showDraftDexTab && dexDetailsToSave && !isDexDraftValid) {
      showToast('danger', 'DEX details are incomplete - please correct them on the DEX tab before saving.');
      setActiveTab(DEX_TAB_INDEX);
      return;
    }

    if (requiresDexCopyConfirmation()) {
      setPendingDexCreateValues({ values, originalValues });
      setDexConfirmOpen(true);
      return;
    }
    await onSave(values, originalValues);
  };

  const onDexCopyConfirm = (confirmed: boolean) => {
    setDexConfirmOpen(false);
    const pending = pendingDexCreateValues;
    setPendingDexCreateValues(null);
    // Declining leaves the user on the form with the DEX tab still populated, so they can correct
    // the values rather than losing the appointment they were part-way through creating.
    if (!confirmed || !pending) return;
    onSave(pending.values, pending.originalValues);
  };

  // DEX data from a "Create Similar" source is now loaded BEFORE the appointment is
  // saved, so it can be shown on the new appointment's DEX tab for review and correction rather
  // than being written silently afterwards onto a record nobody looked at.
  //
  // The skip rules that previously lived in copyDexDetails still apply, and still resolve here:
  // an excluded service type or missing DEX config leaves the source with dex_found=false /
  // is_excluded=true, and a source whose DEX tab was never filled in has a null subcontractor
  // (that field is confirmed on every DEX type - see dex_readiness.mjs's universal rule). In any
  // of those cases nothing is seeded, so the tab renders empty and nothing is copied on save.
  useEffect(() => {
    if (!isNew || !copyDexSourceId) return;
    let cancelled = false;

    const loadSourceDex = async () => {
      const { data: sourceDex, error } = await dispatch(
        dexDetailsApi.endpoints.getDexDetails.initiate({ appointmentId: copyDexSourceId }, { subscribe: false }),
      );
      if (cancelled) return;
      if (error) {
        console.error('Failed to fetch source DEX details for copy', error);
        showToast('danger', 'DEX details could not be loaded from the source appointment — please check the DEX tab.');
        return;
      }
      if (!sourceDex?.dex_found || sourceDex.is_excluded) return;
      // toApiBody coerces an unanswered subcontractor to a definite `false`, which would then
      // satisfy dex_readiness's "confirmed" gate with data nobody entered - so an unfilled source
      // seeds nothing rather than seeding a fabricated answer.
      if (sourceDex.subcontractor == null) return;
      dexSourceValuesRef.current = sourceDex;
      setDexSourceValues(sourceDex);
    };

    loadSourceDex().finally(() => sourceDexSettleRef.current?.());
    return () => {
      cancelled = true;
    };
  }, [isNew, copyDexSourceId, dispatch]);

  // a new appointment carrying a Create Similar DEX copy gets the same DEX tab, in draft
  // mode - so the copied data is reviewable and correctable before the first save rather than being
  // discovered afterwards. The source appointment is only ever CHSP-funded (AppointmentBanner gates
  // the copy on it), which is the same condition hasDexTab applies to a saved appointment.
  const showDraftDexTab = isNew && !isGroupParent && !!copyDexSourceId;

  // Resolved at page level, not inside the DEX tab, and that placement is the whole point. The
  // tab's own query is gated on the tab being active, so a service type changed while the tab was
  // never opened - or opened and then left - leaves the tab's map absent or stale. The page needs
  // the TARGET appointment's real DEX type at submit time regardless of which tab the user looked
  // at, both to mask the values and to honour the excluded / no-config skips.
  const { data: draftFieldMap } = useGetDexFieldMapQuery(
    { service_type_id: (draftServiceTypeId ?? copyValues?.service_type_id) as string | number },
    { skip: !showDraftDexTab || !(draftServiceTypeId ?? copyValues?.service_type_id) },
  );

  useEffect(() => {
    if (draftFieldMap === undefined) return;
    draftFieldMapRef.current = draftFieldMap;
    fieldMapSettleRef.current?.();
  }, [draftFieldMap]);

  // Masked against the target's field tags, never the source's - a service-type change made on the
  // form before saving has to re-filter the values, which is exactly what copyDexDetails' own
  // comment said it was doing. Published draft values are re-masked here too, so a body published
  // for a service type the user has since changed cannot be saved as-is.
  const buildDexDetails = (
    fieldMap: DexFieldMap | null | undefined,
    draft: Partial<DexDetailsBody> | null,
    source: DexDetails | null,
  ) => {
    if (!(fieldMap?.dex_found === true && fieldMap.is_excluded !== true)) return null;
    const values = draft ?? source;
    if (!values) return null;
    return toApiBody(toFormValues(values), fieldMap.field_tags ?? []);
  };

  // For rendering - the confirmation dialog's value list.
  const dexDetailsToSave = useMemo(
    () => buildDexDetails(draftFieldMap, dexDraft, dexSourceValues),
    [draftFieldMap, dexDraft, dexSourceValues],
  );

  // For the save path, which resumes from an await before React has necessarily re-rendered with
  // either value - so it reads the refs rather than this render's closure.
  const resolveDexDetailsForSave = () =>
    buildDexDetails(draftFieldMapRef.current, dexDraftRef.current, dexSourceValuesRef.current);

  const save = async (values: any) => {
    if (isNew) {
      let group_session_id = undefined;
      if (isNewGroup) {
        group_session_id = isNewGroupParent ? 1 : group_id;
      }

      // childValues contains parent_id (which doesn't have a form field), so must be retained when creating a single appt from parent. Legacy garbage (╯°□°）╯︵ ┻━┻
      const finalValues = childValues
        ? { ...childValues, ...values }
        : {
            ...values,
            repeat: isRepeat,
            support_coordination: isSupportCoordination,
            case_management: isCaseManagement,
            group_session_id,
          };

      // the DEX record is created by the same request as the appointment, so the two
      // commit together. Previously the copy was a separate write after create() returned, which is
      // why a failure there left the appointment saved with no DEX data and only a toast to say so.
      const dexDetails = resolveDexDetailsForSave();
      const createValues = dexDetails
        ? {
            ...finalValues,
            // Only attribute the DEX record to the copy source when a copy was genuinely eligible -
            // see hasEligibleDexCopy above. Otherwise these are the draft tab's own (possibly
            // user-entered) values for this new appointment, and the API logs a plain "DEX Details
            // Added" entry rather than a "DEX Details Copied #X" one that never actually happened.
            dex_details: {
              ...dexDetails,
              copied_from_appointment_id: dexSourceValuesRef.current !== null ? (copyDexSourceId ?? null) : null,
            },
          }
        : finalValues;

      const { payload } = await dispatch(create(createValues));
      if (payload?.data?.id) {
        dispatch(clearCopyAppointment());
        navigate(`/appointments/${payload.data.id}`);
      }
    } else {
      // For case management updates, prompt the user about CRM note before saving
      if (isCaseManagement && !('add_crm_note' in values)) {
        setPendingSaveValues(values);
        setCrmNoteOpen(true);
        return;
      }

      const { clients, users, ...rest } = values;
      if (isGroup) {
        const newUsers = (users ?? []).filter((u: any) => u.isNew);
        const newClients = (clients ?? []).filter((c: any) => c.isNew);
        if (newUsers.length || newClients.length) {
          await dispatch(addGroupChildren({ id, clients: newClients, users: newUsers }));
        }
      }
      const { payload } = await dispatch(
        update({
          id,
          ...rest,
        }),
      );
      if (payload?.success) loadExisting();
      // Scroll to top of container on success and failure.
      window.scrollTo(0, 0);
    }
  };

  const onCrmNoteConfirm = (addNote: boolean) => {
    setCrmNoteOpen(false);
    if (pendingSaveValues) {
      save({ ...pendingSaveValues, add_crm_note: addNote });
    }
  };

  const validateConfirm = (values: any, originalValues: any) => {
    if (
      values.user_id === companySettings.SYSTEM_2022_GROUP_SESSION_USER_ID ||
      values.user_id === companyDefaults?.support_coordination_service_id
    ) {
      return true;
    }

    if (!isNew && isCaseManagement && values.notes !== originalValues.notes) {
      setConfirmMessages((prevMessages: string[]) => [
        ...prevMessages,
        'Your Case Management notes will be saved as a new entry. All previous notes will remain unchanged to maintain a complete record.',
      ]);
      return false;
    }

    const definitions = fieldDefinitions.filter(({ show }) => {
      if (typeof show === 'function') return show(values);
      if (show === undefined) return true;
      return show;
    });

    let valid = true;
    definitions.forEach(({ key }) => {
      const definition = fieldDefinitions.find(({ key: fieldKey }) => fieldKey === key);

      if (definition?.validateConfirm) {
        const result = definition.validateConfirm(values, originalValues);
        if (!result.valid) {
          valid = false;
          if (result.message) {
            setConfirmMessages((prevMessages: string[]) => [...prevMessages, result.message]);
          }
        }
      }
    });

    return valid;
  };

  // Determine field definitions to use
  useEffect(() => {
    if (serviceTypes.length > 0) {
      const updatedFields = Object.entries(
        get({
          isNew,
          isRepeat,
          isSupportCoordination,
          isCaseManagement,
          isNewGroup,
          isExistingGroupParent,
          isClientGroupChild,
          participantType,
          ...appointment,
        }),
      ).map(([key, props]) => ({
        key,
        disabled: !canSave(),
        ...props,
      }));

      setFields(updatedFields);
    }
  }, [serviceTypes, isSupportCoordination, isCaseManagement, isGroup, isClientGroupChild]);

  useEffect(() => {
    const newFields = get({
      isNew,
      isRepeat,
      isSupportCoordination,
      isCaseManagement,
      isNewGroup,
      isExistingGroupParent,
      isClientGroupChild,
      participantType,
      ...appointment,
    });
    setFields(newFields);
  }, [
    isNew,
    isRepeat,
    appointment,
    isSupportCoordination,
    isCaseManagement,
    isGroup,
    isClientGroupChild,
    participantType,
  ]);

  const fieldDefinitions = Object.entries(fields).map(([key, props]) => ({
    key,
    disabled: !canSave(),
    ...props,
  }));

  const defaultValues = useMemo(() => {
    const values: { transport: boolean; note_type_id?: number } = {
      transport: isTransport,
    };

    if (isCaseManagement) {
      values.note_type_id = CASE_MANAGEMENT_NOTE_TYPE_ID;
    }

    return values;
  }, [isTransport, isCaseManagement]);

  const overrideValues = useMemo(() => {
    const sanitizedValues: { [key: string]: any } = { ...searchParamValues };

    // Convert *_id values to numbers
    for (const key in sanitizedValues) {
      if (key.endsWith('_id')) {
        const value = sanitizedValues[key];
        const numberValue = Number(value);
        if (!isNaN(numberValue)) {
          sanitizedValues[key] = numberValue;
        } else {
          console.error('Error while converting search param value:', key, value);
        }
      }
    }

    let overrideSearchValues = { ...sanitizedValues };

    // Role-specific overrides
    switch (true) {
      case isSupportCoordination:
        overrideSearchValues = {
          ...formDefaults,
          service_type_id: supportCoordinationId,
          support_coordination: true,
          user_id: authenticatedUser?.id,
          ...overrideSearchValues,
        };
        break;

      case isCaseManagement:
        overrideSearchValues = {
          ...formDefaults,
          case_management: true,
          user_id: isNew ? authenticatedUser?.id : undefined,
          ...overrideSearchValues,
        };
        break;
      default:
        break;
    }

    // Merge child values if parent_id exists
    if (overrideSearchValues.parent_id) {
      return {
        ...childValues,
        ...overrideSearchValues,
      };
    }

    // Final override for new group or copied values
    return {
      ...((isNew || participantType) && copyValues),
      ...overrideSearchValues,

      ...(isNewGroupParent && {
        client_id: companySettings?.SYSTEM_2022_GROUP_SESSION_CLIENT_ID,
        user_id: companySettings?.SYSTEM_2022_GROUP_SESSION_USER_ID,
        user: defaultUser,
        client: defaultClient,
      }),

      ...(isNewGroupChild && {
        client_id: participantType === 'user' && companySettings?.SYSTEM_2022_GROUP_SESSION_CLIENT_ID,
        user_id: participantType === 'client' && companySettings?.SYSTEM_2022_GROUP_SESSION_USER_ID,
        user: defaultUser,
        client: defaultClient,
      }),
      repeat: isRepeat,
    };
  }, [
    isNew,
    copyValues,
    isRepeat,
    childValues,
    searchParamValues,
    isSupportCoordination,
    isCaseManagement,
    companySettings,
    participantType,
    isGroup,
    isNewGroupParent,
    defaultUser,
    defaultClient,
    supportCoordinationId,
    formDefaults,
    authenticatedUser,
  ]);

  const buttonProps = {
    authTag: AuthTags.APPOINTMENTS.UPDATE,
    color: 'success',
    className: 'ms-4 me-4 ps-4 pe-4',
    children: `${isNew ? 'Add' : 'Update'} Appointment`,
    ['data-cy']: 'appointment-submit-button',
  };

  const groupTabs =
    appointment && isExistingGroupParent && !isNew
      ? [
          {
            id: 'group-summary',
            title: 'Summary',
            content: (
              <GroupAppointmentSummary
                appointment={appointment}
                onEditSettings={() => setActiveTab(2)}
                onQuickAdd={(type, item) => {
                  const nextUsers = type === 'users' ? [...(appointment.users ?? []), item] : appointment.users;
                  const nextClients = type === 'clients' ? [...(appointment.clients ?? []), item] : appointment.clients;
                  onSave({ users: nextUsers, clients: nextClients }, appointment);
                }}
              />
            ),
          },
          {
            id: 'group-attendees',
            title: 'Attendees',
            content: (
              <GroupAppointmentAttendees
                appointment={appointment}
                id={id ?? ''}
                isRepeat={isRepeat}
                onSave={(users: any[], clients: any[]) => onSave({ users, clients }, appointment)}
              />
            ),
          },
          {
            id: 'group-settings',
            title: 'Settings',
            content: (
              <GroupAppointmentSettings
                appointment={appointment}
                fieldDefinitions={fieldDefinitions}
                defaultValues={defaultValues}
                overrideValues={overrideValues}
                canSave={canSave}
                onSave={onSave}
                isRepeat={isRepeat}
                authTag={AuthTags.APPOINTMENTS.UPDATE}
                confirmButtonProps={buttonProps}
              />
            ),
          },
        ]
      : null;

  const appointmentFormContent = (
    <AppointmentForm
      authTag={AuthTags.APPOINTMENTS.UPDATE}
      fields={fieldDefinitions}
      defaultValues={defaultValues}
      overrideValues={overrideValues}
      canSave={canSave}
      save={onSaveWithDexConfirmation}
      onValuesChange={isNew ? (values: any) => setDraftServiceTypeId(values?.service_type_id ?? null) : undefined}
      isNew={isNew}
      isGroup={isGroupParent}
      confirmButtonProps={buttonProps}
      showActivityLogs={!isNew && canViewActivities}
      showFullNotes={isCaseManagement}
      isActive={isDetailsActive}
    />
  );

  const singleAppointmentTabs =
    (appointment && !isNew && !isGroupParent && hasDexTab && id) || showDraftDexTab
      ? [
          {
            id: 'appointment-details',
            title: 'Details',
            // Not rendered at all until Details has genuinely been viewed once (see
            // hasMountedDetailsContent above) — this is what actually stops AppointmentForm and its
            // field components' own data fetching from firing on a `?tab=dex` deep link.
            content: hasMountedDetailsContent || isNew ? appointmentFormContent : null,
          },
          {
            id: 'appointment-dex',
            title: 'DEX',
            content: showDraftDexTab ? (
              <AppointmentDexTab
                isDraft
                isActive={activeTab === DEX_TAB_INDEX}
                serviceTypeId={draftServiceTypeId ?? copyValues?.service_type_id ?? null}
                draftInitialValues={dexSourceValues}
                onDraftChange={({ values, fieldTags, isValid }) => {
                  setDexDraft(values);
                  dexDraftRef.current = values;
                  setDexDraftFieldTags(fieldTags ?? []);
                  setIsDexDraftValid(isValid !== false);
                }}
              />
            ) : (
              <AppointmentDexTab appointmentId={id} isActive={activeTab === DEX_TAB_INDEX} />
            ),
          },
        ]
      : null;

  // The activeTab initializer guesses DEX_TAB_INDEX for a `?tab=dex` URL before we know whether this
  // appointment even has a DEX tab. That guess is wrong for an existing group-parent appointment,
  // which always renders groupTabs (never singleAppointmentTabs) — DEX_TAB_INDEX (1) would then
  // coincidentally land on groupTabs' "Attendees" entry instead of its own default, "Summary" (0).
  // Correct it back once we actually know, rather than trying to guess correctly up front.
  const hasCorrectedGroupParentTab = React.useRef(false);
  useEffect(() => {
    if (hasCorrectedGroupParentTab.current || !appointment || !isGroupParent) return;
    hasCorrectedGroupParentTab.current = true;
    if (activeTab === DEX_TAB_INDEX) setActiveTab(0);
  }, [appointment, isGroupParent, activeTab]);

  useEffect(() => {
    if (isNew || !hasMountedDetailsContent) return;
    // hasMountedDetailsContent only ever transitions false -> true once, so this fires exactly once.
    loadDetailsData();
  }, [hasMountedDetailsContent, isNew]);

  const getBannerProps = () => {
    const isRepeat = appointment?.repeat ?? forceRepeat;
    const isGroup = isNewGroup || isExistingGroupParent || isExistingGroupChild;

    // Header class name determination
    const headerClassName = classNames({
      warning: isRepeat,
      info: !isRepeat && !isSupportCoordination && !isCaseManagement && !isExistingGroupParent,
      'support-coordination': isSupportCoordination,
      'case-management': isCaseManagement,
      'group-appointment': isGroup,
      'text-white': isRepeat || isSupportCoordination || isCaseManagement || isGroup,
    });

    // Header text determination
    let headerText;
    switch (true) {
      case isSupportCoordination:
        headerText = `Support Coordination Entry - ${isNew ? 'New' : `Ref #${appointment?.reference_number}`}`;
        break;
      case isCaseManagement:
        headerText = `Case Management Entry - ${isNew ? 'New' : `Ref #${appointment?.reference_number}`}`;
        break;
      case isGroup:
        headerText = `Group Appointment Details - ${isNew ? 'New' : `Ref #${appointment?.reference_number}`}`;
        break;
      case !appointment:
        headerText = `Appointment Details - ${isNew ? 'New' : `Not Found`}`;
        break;
      default:
        headerText = `Appointment Details - Ref #${appointment?.reference_number}`;
    }

    // Data-cy attribute determination
    let dataCy;
    switch (true) {
      case isSupportCoordination:
        dataCy = 'support-coordination-header';
        break;
      case isCaseManagement:
        dataCy = 'case-management-header';
        break;
      case isGroup:
        dataCy = 'group-header';
        break;
      case isRepeat:
        dataCy = 'repeat-appointments-header';
        break;
      default:
        dataCy = 'appointment-header';
    }

    // Return to determination
    let returnTo;
    switch (true) {
      case (isNewGroup || isExistingGroupParent) && !isRepeat:
        returnTo = 'groups';
        break;
      case isRepeat:
        returnTo = 'repeats';
        break;
      default:
        returnTo = 'appointments';
    }

    // Icon color determination
    let iconColor;
    if (isSupportCoordination || isCaseManagement || isRepeat) {
      iconColor = 'white';
    }

    return {
      headerClassName,
      headerText,
      dataCy,
      iconColor,
      formDefaults: companyFormDefaults,
      showRepeatLabel: isRepeat,
      showTransportLabel: isTransport,
      isNew,
      onBackNavigatesTo: returnTo,
      isCaseManagement,
    };
  };

  return (
    <div className="p-2">
      <ConfirmDialog
        isOpen={confirmOpen}
        messages={confirmMessages}
        onClose={(confirm) => onConfirm(confirm, changedValues)}
      />
      <ConfirmDialog
        isOpen={crmNoteOpen}
        messages="Do you want to add a new note into the CRM from this appointment?"
        onClose={onCrmNoteConfirm}
        useList={false}
      />
      <DexCopyConfirmDialog
        isOpen={dexConfirmOpen}
        sourceReference={
          (copyDexSourceReference as string | null) ?? (copyDexSourceId as number | string | null) ?? null
        }
        values={dexDetailsToSave}
        fieldTags={dexDraftFieldTags.length > 0 ? dexDraftFieldTags : (dexSourceValues?.field_tags ?? [])}
        onClose={onDexCopyConfirm}
      />
      <AppointmentBanner {...getBannerProps()} />
      <ErrorResponse message={errorResponse} dataCy="appointments-errorResponse" />
      {isPendingDeclined && (
        <AppointmentDeclined
          appointment={appointment}
          onAcknowledge={() => save({ chargeable_cancellation_actioned: true })}
        />
      )}
      <ContainerLoading loading={isLoading || isRedirecting} bgColor="white" color="black" isTransparent={true}>
        {!isLoading && !isRedirecting && (
          <>
            {appointment && isExistingGroupParent && !isNew && groupTabs && (
              <TabContainer tabs={groupTabs} setPageTitle={false} activeTab={activeTab} onTabChange={setActiveTab} />
            )}
            {(!appointment || !isExistingGroupParent || isNew) && (
              <>
                {(appointment || isNew) && singleAppointmentTabs && (
                  <TabContainer
                    tabs={singleAppointmentTabs}
                    setPageTitle={false}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                  />
                )}
                {(appointment || isNew) && !singleAppointmentTabs && appointmentFormContent}
                {!appointment && !isNew && <PageNotFound content={getMissingContent('appointment')} />}
              </>
            )}
          </>
        )}
      </ContainerLoading>
    </div>
  );
};

export default Appointment;
